import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../shared/material.module';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { TranscodeService } from '../../../../services/transcode.service';
import { Transcode, TranscodeDTO } from '../../../../models/transcode.model';
import { VehicleUtils } from '../../../../utils/vehicle.utils';

@Component({
  selector: 'app-step-accident-dynamics',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslatePipe,
  ],
  templateUrl: './step-accident-dynamics.component.html',
  styleUrl: './step-accident-dynamics.component.scss',
})
export class StepAccidentDynamicsComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() plate?: string;
  @Input() involvedVehicles: any[] = [];

  vehicleOptions: Transcode[] = [];
  showAllStates: { [key: string]: boolean } = {};
  selectedCircumstances: { [key: string]: string[] } = {};
  vehicleLetters = VehicleUtils.getVehicleLetters();
  limitShowElement = 8;

  constructor(
    private fb: FormBuilder,
    private transcodeService: TranscodeService,
  ) {}

  ngOnInit() {
    this.initializeDynamicsForm();
    this.loadSavedDynamics();
    this.loadCircumstances();
  }

  private initializeDynamicsForm() {
    if (!this.form.get('accidentDynamics')) {
      const dynamicsGroup: any = { vehicleA: [[], Validators.required] };
      this.involvedVehicles.forEach((_, index) => {
        const vehicleLetter = this.vehicleLetters[index + 1];
        dynamicsGroup[`vehicle${vehicleLetter}`] = [[], Validators.required];
      });
      this.form.addControl('accidentDynamics', this.fb.group(dynamicsGroup));
    }
  }

  private loadSavedDynamics() {
    const savedDynamics = this.form.get('accidentDynamics')?.value;
    if (savedDynamics) {
      Object.keys(savedDynamics).forEach(key => {
        const vehicleLetter = key.replace('vehicle', '');
        this.selectedCircumstances[vehicleLetter] = savedDynamics[key] || [];
      });
    }
  }

  private loadCircumstances() {
    this.transcodeService.getTranscodes('CID_CIRCUMSTANCE').subscribe((circumstances: TranscodeDTO[]) => {
      this.vehicleOptions = circumstances;
    });
  }

  onVehicleChange(vehicleLetter: string, optionId: string, event: any) {
    if (!this.selectedCircumstances[vehicleLetter]) {
      this.selectedCircumstances[vehicleLetter] = [];
    }
    if (event.checked) {
      this.selectedCircumstances[vehicleLetter].push(optionId);
    } else {
      this.selectedCircumstances[vehicleLetter] = this.selectedCircumstances[vehicleLetter].filter(id => id !== optionId);
    }
    this.form.get(`accidentDynamics.vehicle${vehicleLetter}`)?.setValue([...this.selectedCircumstances[vehicleLetter]]);
  }

  getSelectedCount(vehicleLetter: string): number {
    return this.selectedCircumstances[vehicleLetter]?.length || 0;
  }

  getVisibleOptions(vehicleLetter: string): Transcode[] {
    const showAll = this.showAllStates[vehicleLetter];
    return showAll ? this.vehicleOptions : this.vehicleOptions.slice(0, this.limitShowElement);
  }

  isOptionSelected(vehicleLetter: string, optionId: string): boolean {
    return this.selectedCircumstances[vehicleLetter]?.includes(optionId) || false;
  }

  getVehicleHeaderClass(vehicleIndex: number): string {
    const letter = this.vehicleLetters[vehicleIndex].toLowerCase();
    return `vehicle-${letter}-header`;
  }

  isStepValid(): boolean {
    const vehicleAValid = this.selectedCircumstances['A']?.length > 0;
    const otherVehiclesValid = this.involvedVehicles.every((_, index) => {
      const vehicleLetter = this.vehicleLetters[index + 1];
      return this.selectedCircumstances[vehicleLetter]?.length > 0;
    });
    return vehicleAValid && otherVehiclesValid;
  }

  getDynamicsData(): any {
    const result: any = {};
    result.vehicleA = {
      plate: this.plate,
      circumstances: this.selectedCircumstances['A'] || []
    };
    result.vehicleB = this.involvedVehicles.map((vehicle, index) => ({
      plate: vehicle.plate,
      circumstances: this.selectedCircumstances[this.vehicleLetters[index + 1]] || []
    }));
    return result;
  }
}
